'use client';

import { useEffect } from 'react';
import posthog from 'posthog-js';
import { useGeoforgeStore } from '@/components/providers/geoforge-store-provider';
import { useCookiebotStatisticsDeclined } from '@/hooks/use-cookiebot-statistics-consent';

/**
 * Tracks user identity in PostHog
 *
 * Features:
 * - Identifies users when they log in (links anonymous activity to user profile)
 * - Sets person properties (email, name)
 * - Resets tracking on logout
 *
 * Mirrors the capture gate in CSPostHogProvider: identity tracking runs by
 * default and is skipped only when the user has explicitly declined statistics
 * cookies via the Cookiebot banner.
 *
 * This component must be mounted as a child of CSPostHogProvider to ensure
 * PostHog is initialized before attempting to identify users
 */
export default function PosthogUserTracker() {
  const currentUser = useGeoforgeStore(state => state.currentUser);
  const hasExplicitlyDeclined = useCookiebotStatisticsDeclined();

  useEffect(() => {
    if (typeof window === 'undefined' || hasExplicitlyDeclined || !posthog.__loaded) {
      return;
    }

    if (currentUser) {
      posthog.identify(currentUser.id, {
        email: currentUser.email,
        name: currentUser.display_name || currentUser.email.split('@')[0],
      });
    } else {
      posthog.reset();
    }
  }, [currentUser, hasExplicitlyDeclined]);

  return null;
}
